import type { DocumentHighlightRequest } from '@/providers/ChatDocumentViewerContext';
import type { Region } from '@/types/documentModel';

/**
 * - `active`: the facts the student just tapped (the requested page's regions).
 * - `all-for-item`: the same item's regions on other pages.
 * - `dimmed`: what was active before this request, kept faintly for context.
 */
export type HighlightState = 'active' | 'all-for-item' | 'dimmed';

export interface HighlightBox {
  key: string;
  region: Region;
  state: HighlightState;
}

const regionKey = ({ page, x, y, w, h }: Region): string =>
  `${page}:${x}:${y}:${w}:${h}`;

/**
 * The boxes to draw after a new open request. A request without regions (a
 * plain page citation) clears everything. Otherwise the requested page's
 * regions are active (all of them when no page was asked for), the rest of
 * the item's are `all-for-item`, and the previous request's active boxes stay
 * as `dimmed` unless the new request draws the same box. An `item`-scoped
 * request draws every region as `all-for-item`.
 */
export const nextHighlightBoxes = (
  previous: HighlightBox[],
  request: DocumentHighlightRequest | null | undefined
): HighlightBox[] => {
  if (!request || request.regions.length === 0) return [];

  const seen = new Set<string>();
  const next: HighlightBox[] = [];

  for (const region of request.regions) {
    const key = regionKey(region);
    if (seen.has(key)) continue;
    seen.add(key);

    next.push({
      key,
      region,
      state:
        request.scope !== 'item' &&
        (request.page === null || region.page === request.page)
          ? 'active'
          : 'all-for-item',
    });
  }

  for (const box of previous) {
    if (box.state !== 'active' || seen.has(box.key)) continue;
    seen.add(box.key);
    next.push({ ...box, state: 'dimmed' });
  }

  return next;
};

/** The boxes grouped by page, for each page slot to draw its own. */
export const groupHighlightsByPage = (
  boxes: HighlightBox[]
): Map<number, HighlightBox[]> => {
  const byPage = new Map<number, HighlightBox[]>();

  for (const box of boxes) {
    const list = byPage.get(box.region.page);
    if (list) list.push(box);
    else byPage.set(box.region.page, [box]);
  }

  return byPage;
};

const clampFraction = (value: number): number =>
  Math.min(1, Math.max(0, value));

/**
 * A region as percentage offsets within its page, clipped to the page.
 * Percentages of the page box need no recomputation when the zoom changes.
 */
export const regionToPercentStyle = ({
  x,
  y,
  w,
  h,
}: Region): { left: string; top: string; width: string; height: string } => {
  const left = clampFraction(x);
  const top = clampFraction(y);
  const right = clampFraction(x + w);
  const bottom = clampFraction(y + h);
  const percent = (value: number) => `${+(value * 100).toFixed(4)}%`;

  return {
    left: percent(left),
    top: percent(top),
    width: percent(Math.max(0, right - left)),
    height: percent(Math.max(0, bottom - top)),
  };
};

/**
 * Where a request should scroll: the topmost active region on its page, as
 * a fraction down that page, or null when there is nothing to aim at.
 */
export const firstActiveRegionOffset = (
  request: DocumentHighlightRequest | null | undefined
): number | null => {
  if (!request || request.page === null) return null;

  const offsets = request.regions
    .filter((region) => region.page === request.page)
    .map((region) => clampFraction(region.y));

  return offsets.length > 0 ? Math.min(...offsets) : null;
};
